import React, { createContext, useContext } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { theme } from '../../../core/design-system/theme'
import { StepBar } from '../../../components/shared/StepBar'
import { useBookingViewModel } from '../hooks/useBookingViewModel'
import BookingStep1View from './steps/BookingStep1View'
import BookingStep2View from './steps/BookingStep2View'
import BookingStep3View from './steps/BookingStep3View'
import BookingStep4View from './steps/BookingStep4View'
import BookingStep5View from './steps/BookingStep5View'
import BookingStep6View from './steps/BookingStep6View'

const prefersReducedMotion =
  typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false

const BookingContext = createContext(null)

export function useBookingContext() {
  return useContext(BookingContext)
}

export function BookingStepShell({
  step,
  title,
  children,
  onContinue,
  continueLabel = 'Continue',
  continueDisabled = false,
  onBack,
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.colors.cream,
        paddingBottom: 90,
        fontFamily: 'var(--font-body)',
        position: 'relative',
      }}
    >
      <button
        onClick={onBack}
        aria-label="Go back"
        style={{
          position: 'absolute',
          top: 16,
          left: 16,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: theme.colors.textSecondary,
          padding: 8,
          lineHeight: 0,
          zIndex: 1,
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      <div style={{ padding: '56px 20px 0' }}>
        <StepBar current={step} total={6} />
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 26,
            color: theme.colors.textPrimary,
            margin: '20px 0 24px',
            fontWeight: 700,
          }}
        >
          {title}
        </h1>
        {children}
      </div>

      <div
        style={{
          position: 'fixed',
          bottom: 64,
          left: 0,
          right: 0,
          padding: '12px 20px',
          background: theme.colors.cream,
          borderTop: `1px solid ${theme.colors.border}`,
          zIndex: 10,
        }}
      >
        <button
          onClick={onContinue}
          disabled={continueDisabled}
          style={{
            width: '100%',
            background: theme.colors.terracotta,
            color: '#fff',
            border: 'none',
            borderRadius: theme.layout.buttonRadius,
            padding: '16px',
            fontSize: 16,
            fontWeight: 700,
            fontFamily: 'var(--font-body)',
            cursor: continueDisabled ? 'not-allowed' : 'pointer',
            opacity: continueDisabled ? 0.4 : 1,
            transition: prefersReducedMotion ? 'none' : 'opacity 0.15s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          {continueLabel}
        </button>
      </div>
    </div>
  )
}

export default function BookingFlowView({ repository, session }) {
  const viewModel = useBookingViewModel()

  return (
    <BookingContext.Provider value={{ viewModel, repository, session }}>
      <Routes>
        <Route index element={<Navigate to="/book/info" replace />} />
        <Route path="info" element={<BookingStep1View />} />
        <Route path="children" element={<BookingStep2View />} />
        <Route path="datetime" element={<BookingStep3View />} />
        <Route path="select" element={<BookingStep4View />} />
        <Route path="review" element={<BookingStep5View />} />
        <Route path="pay" element={<BookingStep6View />} />
      </Routes>
    </BookingContext.Provider>
  )
}
